import { usePricingContext } from '@vp/react-pricing-context';
import { useEffect, useState } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxPopover,
    ComboboxSection,
} from '@vp/swan';
import { Currency, price, useIdentityContext } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { sendProductSelectedTrackingEvent, usePage } from '@99designs/tracking';
import { useCart } from '../../../../../lib/hooks/useCart';
import { useBriefContext } from '../../../../BriefContext/BriefContext';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import useUpdateProductDropdown from '../ProductDropdown/useUpdateProductDropdown';
import { useProductOptions } from '../ProductOptions';
import { ClientConfiguredFieldProps } from '../types';
import { useTopProductsQuery } from './products.generated';
import {
    Product,
    RecentlySelectedProduct,
    categoryBriefMap,
    mapCategoriesToProducts,
    transformBriefProductToProduct,
} from './utils';

export function Input({
    id,
    clientConfiguredValue,
    groupId,
    setValue,
    register,
    clearErrors,
}: ClientConfiguredFieldProps) {
    const { locale, pageData } = usePage();
    const { setProductOptionTouched, setSelectedOptions } = useProductOptions();
    const { authorizationHeader, shopperId, isSignedIn } = useIdentityContext();
    const {
        product: productFromBriefContext,
        isUpdatingProduct,
        setIsUpdatingProduct,
        recentProducts,
        addRecentProduct,
    } = useBriefContext();
    const { briefId } = useBriefFormContext();
    const pricingContext = usePricingContext();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [selectedKey, setSelectedKey] = useState<string>(clientConfiguredValue.toLowerCase());

    const { data, loading: isTopProductsQueryLoading } = useTopProductsQuery();
    const { isInCart } = useCart(locale, { authorizationHeader, shopperId });

    const selectedServiceMpvId = data?.topProducts.find(
        (v) =>
            v.product.mpvId.toLowerCase() === selectedKey.toLowerCase() ||
            v.product.esMpvId?.toLowerCase() === selectedKey.toLowerCase()
    )?.product;

    const updateMutation = useUpdateProductDropdown(
        id,
        briefId,
        selectedKey,
        groupId,
        selectedServiceMpvId?.esMpvId
    );

    useEffect(() => {
        const updateProductAsync = async () => {
            setIsUpdatingProduct(true);
            await updateMutation(selectedKey);
            setIsUpdatingProduct(false);
        };
        void updateProductAsync();
    }, [selectedKey, updateMutation, setIsUpdatingProduct]);

    useEffect(() => {
        async function disableIfBriefIsInCart() {
            const briefInCart = await isInCart(briefId as string);
            setIsDisabled(briefInCart);
        }
        if (isSignedIn) {
            void disableIfBriefIsInCart();
        }
    }, [briefId, isInCart, isSignedIn]);

    // LOGIC

    useEffect(() => {
        if (
            data &&
            productFromBriefContext &&
            !recentProducts
                ?.map((product) => product.mpvId.toLowerCase())
                .includes(productFromBriefContext.fulfilmentMpvId?.toLowerCase() || '')
        ) {
            // Only add recent products if it is not found in the top products
            // list. This ensures that a reference can be found in the dropdown
            // for products not in the top products, i.e. when you start with a
            // product not in the dropdown, but doesn't cause issues whereby
            // service names are added to the recent products that are already
            // in the top products. I don't think customer UX is greatly
            // affected by this change and it solves an issue whereby we get
            // weird selection behaviour because it's the service name of the
            // product that's the label in the product card.
            if (selectedKey && !selectedServiceMpvId) {
                addRecentProduct(transformBriefProductToProduct(productFromBriefContext));
            }
        }
    }, [
        addRecentProduct,
        productFromBriefContext,
        recentProducts,
        selectedServiceMpvId,
        selectedKey,
        data,
    ]);

    useEffect(() => {
        setIsLoading(isTopProductsQueryLoading || isUpdatingProduct);
    }, [isTopProductsQueryLoading, isUpdatingProduct]);

    useEffect(() => {
        setValue(id, selectedKey);
        clearErrors(id);
    }, [id, selectedKey, setValue, clearErrors]);

    if (!data) {
        return (
            <Combobox mb="5" key="loading-dropdown-msb">
                <ComboboxInput placeholder={__('Select product')} loadingShimmer />
            </Combobox>
        );
    }

    const [sortedCategoryMap] = mapCategoriesToProducts(data);

    const handleChange = async (selection: string) => {
        // Don't update if the same product is already selected
        if (!selection || selection === selectedKey) {
            return;
        }

        setSelectedKey(selection);
        setSelectedOptions({});
        setProductOptionTouched();

        sendProductSelectedTrackingEvent({
            ...pageData,
            category: 'Design Services',
        });

        return;
    };
    const comboBoxItems = [
        ...Array.from(sortedCategoryMap.keys()).map((category) => (
            <ComboboxSection title={categoryBriefMap(category) || category} key={category}>
                {sortedCategoryMap
                    .get(category)
                    ?.map((product: Product) => (
                        <ComboboxOption key={product.mpvId.toLowerCase()}>
                            {productOptionLabel(product, pricingContext?.vatInclusive)}
                        </ComboboxOption>
                    )) ?? []}
            </ComboboxSection>
        )),
        <ComboboxSection title={__('Recently Selected')} key={'recently-selected'}>
            {recentProducts?.map((product: RecentlySelectedProduct) => (
                <ComboboxOption key={product.mpvId.toLowerCase()}>
                    {productOptionLabel(product, pricingContext?.vatInclusive)}
                </ComboboxOption>
            )) ?? []}
        </ComboboxSection>,
    ];

    return (
        <Combobox
            mb="5"
            {...register(id, {
                value: selectedKey,
                required: __('Please select a product type', {
                    _context: 'This is the error message for product selection',
                }),
            })}
            selectedKey={selectedKey}
            onSelectionChange={(selection) => handleChange((selection as string).toLowerCase())}
            menuTrigger="focus"
        >
            <ComboboxInput
                placeholder={__('Select product')}
                loadingShimmer={isLoading}
                disabled={isDisabled || isLoading}
                data-testid="product-dropdown"
                aria-required={true}
                aria-labelledby={id}
            />
            <ComboboxPopover>
                <ComboboxList>{comboBoxItems}</ComboboxList>
            </ComboboxPopover>
        </Combobox>
    );
}

function productOptionLabel(product: Product | RecentlySelectedProduct, includeTax?: boolean) {
    const p = product.price(includeTax);
    const formattedPrice = price({
        amount: p.amountInCents,
        currency: p.currency as Currency,
        decimalPlaces: 2,
    });
    return `${product.name} - ${formattedPrice}`;
}
